import { useState } from 'react';
import { toast } from 'react-toastify';
import { Stack, Typography } from '@mui/material';
import useUser from '../../../hooks/global/useUser';
import useFeatureGuard from '../../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../../types/subscriptions';
import { LoadedUser } from '../../../interfaces';
import SettingsSection from '../SettingsSection';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  UpdateBusinessPayload,
  updateBusinessSchema,
} from '../../../types/businesses';
import SectionDivider from '../SectionDivider';
import useUpdateBusiness from '../../../hooks/businesses/useUpdateBusiness';

type AfterCareState = Pick<UpdateBusinessPayload, 'tattooAftercareLink'>;
type ReviewLinkState = Pick<UpdateBusinessPayload, 'reviewLink'>;

function ClientMessagesSettings() {
  const [form, setForm] = useState<FormData | null>(null);

  const { user } = useUser<LoadedUser>();
  const hasReviewPage = useFeatureGuard(Feature.MARKETING);
  const { mutateAsync: updateBusiness } = useUpdateBusiness({
    onSuccess: () => {
      setForm(null);
    },
    step: 'BOOKING_MESSAGES',
  });

  if (!user?.business) return null;

  const { business } = user;

  const updateAftercareLink = async (aftercareLink: string) => {
    await updateBusiness({
      tattooAftercareLink: aftercareLink,
    });
  };

  const updateReviewLink = async (reviewLink: string) => {
    await updateBusiness({
      reviewLink,
    });
  };

  const updateReviewLinksEnabled = async (reviewLinksEnabled: boolean) => {
    await updateBusiness({
      reviewLinksEnabled,
    });
  };

  const afterCareForm: FormData<AfterCareState> = {
    initialValues: {
      tattooAftercareLink: business.tattooAftercareLink || '',
    },
    validationSchema: updateBusinessSchema,
    title: 'Edit aftercare link',
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Aftercare link',
          name: 'tattooAftercareLink',
          placeholder: 'https://example.com/aftercare',
        },
      ],
    ],
    onSubmit: async (values: AfterCareState) => {
      await updateAftercareLink(values.tattooAftercareLink || '');
    },
  };

  const reviewLinkForm: FormData<ReviewLinkState> = {
    initialValues: {
      reviewLink: business.reviewLink || '',
    },
    validationSchema: updateBusinessSchema,
    title: 'Edit Google review URL',
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Google review URL',
          name: 'reviewLink',
          placeholder: 'https://g.page/r/your-business/review',
        },
      ],
    ],
    onSubmit: async (values: ReviewLinkState) => {
      await updateReviewLink(values.reviewLink || '');
    },
  };

  return (
    <>
      <SettingsSection
        title="Appointment Reminders"
        subtitle="Messages below will automatically be sent to clients before their appointment. These are default messages, but they will get slightly modified if an appointment has not yet been confirmed, or a waiver needs to be completed."
        rows={[
          {
            title: 'SMS reminder - 48 hours before appointment',
            label:
              'This is a reminder for your upcoming appointment with {{artistName}}. Your appointment is scheduled for {{day}}, from {{startTime}} - {{endTime}} {{timezone}}',
            tooltip:
              'This reminder will be sent to clients 48 hours before their scheduled appointment time. This reminder can not be edited at this time.',
          },
          {
            title: 'SMS reminder - 1 hour before appointment',
            label:
              'This is a reminder for your upcoming appointment with {{artistName}}. Your appointment is scheduled for {{day}}, from {{startTime}} - {{endTime}} {{timezone}}',
            tooltip:
              'This reminder will be sent to clients 1 hour before their scheduled appointment time. This reminder can not be edited at this time.',
          },
        ]}
      />
      <SectionDivider />
      <SettingsSection
        title="Post Appointment Messages"
        subtitle="The following messages can be enabled or disabled. Customers will receive an aftercare link immediately after their appointment is checked out, and one hour later they'll receive a link to leave you a review."
        rows={[
          {
            title: 'Aftercare instructions',
            label: `Hi {{customerName}}! Thanks for visiting ${
              business.name || '...'
            }. Check out our tattoo aftercare instructions through the link below. Feel free to reach out if you have any questions. {{aftercareLink}}`,
            action: {
              type: 'switch',
              checked: !!business.tattooAftercareLink,
              onChange: async checked => {
                if (checked) {
                  toast.info(
                    'To enable the aftercare instructions text message, add an aftercare link below.',
                  );
                } else {
                  await updateAftercareLink('');
                }
              },
            },
            tooltip:
              'If enabled, this text message will be sent to clients immediately after an appointment is checked out or marked as completed through Porter',
          },
          {
            title: 'Aftercare link',
            label: business.tattooAftercareLink || 'No link set',
            isLabelUserInput: !!business.tattooAftercareLink,
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => setForm(afterCareForm),
            },
          },
          {
            title: 'Request a review',
            label: `👋 {{customerName}}! This is {{artistName}} from ${
              business.name || '...'
            }. Thank you so much for your appointment with us. If you had a great experience, would you consider leaving us a Google Review? If you didn't, please let us know how we could do better! Thank you again! {{reviewLink}}`,
            action: {
              type: 'switch',
              checked: business.reviewLinksEnabled,
              onChange: async checked => {
                if (checked && !hasReviewPage && !business.reviewLink) {
                  toast.info(
                    'To enable review requests, add a Google review URL below.',
                  );
                } else {
                  await updateReviewLinksEnabled(checked);
                }
              },
            },
          },
          {
            title: 'Google review URL',
            label: (
              <Stack mt={0.5}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {business.reviewLink || 'No link set'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {hasReviewPage
                    ? 'Customers land on your Porter review page first and are sent to this link after a positive rating.'
                    : 'Customers are sent straight to this link.'}
                </Typography>
              </Stack>
            ),
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => setForm(reviewLinkForm),
            },
          },
        ]}
      />
      <FormSmallModal form={form} onClose={() => setForm(null)} open={!!form} />
    </>
  );
}

export default ClientMessagesSettings;
